import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import { LocationsService } from '../../hooks/LocationsService';
import { SensorDevicesService } from '../../hooks/SensorDevicesService';
import { AnimalType } from '../../models/animal';
import { LocationStable, LocationStableTranslations } from '../../models/locations';
import { SensorDevice, SensorDeviceLocationChangeReason, formatBoundPen } from '../../models/sensor-devices';
import { extractApiErrorMessage, notifications } from '../../utils';

interface PenOption {
  id: string;
  /**
   * Room and pen, the same way the table's "Bound to" column reads them. Self-contained on purpose: this
   * string is also what the success toast and the occupied badge name, and those have no group header above
   * them to borrow the room from.
   *
   * Not prefixed with the word "Pen" - the pen field is free text and a farm that has typed "Pen 1" into it
   * would read "Pen Pen 1".
   */
  label: string;
  number: string | null;
  /** The stable, so the picker groups the way the barn is organised. The room is already in the label. */
  group: string;
  /** Name of the device already bound here, or null when the pen is free. */
  occupiedBy: string | null;
}

/**
 * The two answers, worded as the question a farmer can actually answer. The enum names are no help here: both
 * of them describe a device that is now in a different pen, and only the story behind it says what should
 * happen to the readings already stored.
 */
const reasonOptions: {
  reason: SensorDeviceLocationChangeReason;
  labelKey: string;
  hintKey: string;
}[] = [
  {
    reason: 'Correction',
    labelKey: 'f-sensor-dev-dlg-reason-correction',
    hintKey: 'f-sensor-dev-dlg-reason-correction-hint'
  },
  {
    reason: 'Relocation',
    labelKey: 'f-sensor-dev-dlg-reason-relocation',
    hintKey: 'f-sensor-dev-dlg-reason-relocation-hint'
  }
];

interface SensorDeviceLocationDialogProps {
  device: SensorDevice;
  /** Every device on the farm - used to mark the pens that already hold one. */
  devices: SensorDevice[];
  farmId: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Binds a device to a pen, and asks why it moved.
 *
 * The reason is the part that matters. `Correction` re-stamps the readings this device has already sent onto
 * the new pen - right when the previous binding was a wrong guess, which is the common case, because the
 * backend binds a newly discovered device to an arbitrary free pen. `Relocation` leaves those readings on the
 * pen they were measured in - right when somebody carried the box to another barn. Choosing the wrong one
 * silently misattributes climate history, and nothing afterwards makes that visible, so the copy carries the
 * consequence rather than the enum name.
 *
 * Pens that already hold a device are shown but not selectable: the backend answers that with a 409, and
 * finding out by failing teaches the farmer nothing about which device is in the way.
 */
const SensorDeviceLocationDialog: React.FC<SensorDeviceLocationDialogProps> = ({
  device,
  devices,
  farmId,
  onClose,
  onSaved
}) => {
  const { t } = useTranslation();

  const [pens, setPens] = React.useState<PenOption[]>([]);
  const [isLoadingPens, setIsLoadingPens] = React.useState(false);
  const [selectedPen, setSelectedPen] = React.useState<PenOption | null>(null);
  const [reason, setReason] = React.useState<SensorDeviceLocationChangeReason>('Correction');
  const [isSaving, setIsSaving] = React.useState(false);
  /** Kept in the dialog rather than toasted: a 409 names the pen the farmer just picked, next to the picker. */
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  /** Which pen holds which device, minus this one - moving a device to the pen it is already in is a no-op. */
  const occupiedPens = React.useMemo(() => {
    const byLocation = new Map<string, string>();

    for (const other of devices) {
      if (other.id !== device.id && other.locationId) {
        byLocation.set(other.locationId, other.externalName);
      }
    }

    return byLocation;
  }, [devices, device.id]);

  React.useEffect(() => {
    const service = new LocationsService();
    let cancelled = false;

    setIsLoadingPens(true);

    // The whole hierarchy rather than a page of the search: a picker that pages is a picker the farmer has to
    // guess their way through, and a farm's pens number in the hundreds at most.
    service
      .getLocationsHierarchy(LocationStable.Unknown, AnimalType.Unknown)
      .then((result) => {
        if (cancelled) return;

        const options: PenOption[] = [];

        for (const stableGroup of result.stables ?? []) {
          const stableLabel = t(LocationStableTranslations[stableGroup.stable] ?? 'f-loc-stable-unk');

          for (const roomGroup of stableGroup.rooms ?? []) {
            for (const location of roomGroup.locations ?? []) {
              // A deactivated pen holds no animals, so a device bound to it would measure nothing anyone reads.
              if (location.isSoftDeleted) continue;

              const label = [roomGroup.room, location.pen].filter((part) => !!part).join(' / ');

              options.push({
                id: location.id,
                label: label || location.number || location.id,
                number: location.number || null,
                group: stableLabel,
                occupiedBy: occupiedPens.get(location.id) ?? null
              });
            }
          }
        }

        setPens(options);
        setSelectedPen(options.find((option) => option.id === device.locationId) ?? null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setPens([]);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingPens(false);
      });

    return () => {
      cancelled = true;
    };
  }, [device.id, device.locationId, occupiedPens, t]);

  const handleSave = async () => {
    if (!selectedPen || isSaving) return;

    setIsSaving(true);
    setErrorMessage(null);

    try {
      await new SensorDevicesService().assignLocation(farmId, device.id, selectedPen.id, reason);
      notifications.success(t('f-sensor-dev-assign-ok', { pen: selectedPen.label }));
      onSaved();
    } catch (e: unknown) {
      // The backend's own message names the device occupying the pen, or says why a device cannot hold one.
      // Either is worth more than a generic failure, so it is shown verbatim.
      setErrorMessage(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
    } finally {
      setIsSaving(false);
    }
  };

  const currentPen = formatBoundPen(device);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="sensor-device-location-title">
      <DialogTitle id="sensor-device-location-title">{t('f-sensor-dev-dlg-title')}</DialogTitle>

      <DialogContent>
        <div className="space-y-5 pt-1">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-medium text-gray-500">{t('f-sensor-dev-dlg-device')}</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{device.externalName || t('f-com-unknown')}</p>
            <p className="mt-0.5 text-xs text-gray-500">
              {currentPen ? t('f-sensor-dev-dlg-current-pen', { pen: currentPen }) : t('f-sensor-dev-unbound')}
            </p>
          </div>

          <Autocomplete
            options={pens}
            value={selectedPen}
            onChange={(_event, value) => {
              setSelectedPen(value);
              setErrorMessage(null);
            }}
            loading={isLoadingPens}
            disabled={isSaving}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionDisabled={(option) => option.occupiedBy !== null}
            noOptionsText={t('f-sensor-dev-dlg-pen-none')}
            loadingText={t('f-com-loading')}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <span className="flex w-full items-center justify-between gap-3">
                  <span className="flex flex-col">
                    <span className="text-sm text-gray-900">{option.label}</span>
                    {option.number && <span className="font-mono! text-[11px] text-gray-400">{option.number}</span>}
                  </span>
                  {option.occupiedBy && (
                    <span className="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-500">
                      {t('f-sensor-dev-dlg-pen-occupied', { device: option.occupiedBy })}
                    </span>
                  )}
                </span>
              </li>
            )}
            // Spread straight onto a v9 TextField. `AutocompleteInput` exists for the v4 TextField, which
            // silently drops the `slotProps` bag v9 passes here - this one understands it.
            renderInput={(params) => (
              <TextField {...params} label={t('f-sensor-dev-dlg-pen')} variant="outlined" size="small" />
            )}
          />

          <div>
            <p className="text-sm font-medium text-gray-900">{t('f-sensor-dev-dlg-reason')}</p>
            <RadioGroup
              value={reason}
              onChange={(event) => setReason(event.target.value as SensorDeviceLocationChangeReason)}
            >
              {reasonOptions.map((option) => (
                <FormControlLabel
                  key={option.reason}
                  value={option.reason}
                  disabled={isSaving}
                  control={<Radio size="small" />}
                  className="!mt-2 !items-start"
                  label={
                    <span className="flex flex-col py-1">
                      <span className="text-sm text-gray-900">{t(option.labelKey)}</span>
                      <span className="text-xs text-gray-500">{t(option.hintKey)}</span>
                    </span>
                  }
                />
              ))}
            </RadioGroup>
          </div>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary" disabled={isSaving}>
          {t('f-com-cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!selectedPen || isSaving || isLoadingPens}
        >
          {t('f-sensor-dev-dlg-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SensorDeviceLocationDialog;
